<script setup lang="ts">
  import { computed } from 'vue';
  import type { FormElement, IndexerTitleProperties } from '../../../model/form.types';
  import { useLabelPosition } from '../../../model/useLabelPosition';
  import { useElementContainerStyles } from '../../../model/useElementContainerStyles';

  interface Props {
    element: FormElement & { properties: IndexerTitleProperties };
  }

  const props = defineProps<Props>();

  const { isLabelLeft, inputClasses, labelClasses, labelStyles } = useLabelPosition(
    computed(() => props.element.properties)
  );

  const { containerStyles } = useElementContainerStyles(
    computed(() => props.element.properties),
    'INDEXER_TITLE'
  );

  const displayValue = computed(() => {
    if (props.element.properties.indexerType === 'USER_DEFINED') {
      return props.element.properties.value ?? '';
    }
    return '';
  });

  const labelStyle = computed(() => ({
    color: props.element.properties.color ?? '#2f2f2f',
  }));

  const logFieldNameAttr = computed(() => props.element.properties.logFieldName ?? undefined);
</script>

<template>
  <div
    class="indexer-title-element"
    :class="{ 'label-left': isLabelLeft }"
    :style="containerStyles"
  >
    <label
      :for="`input-${element.id}`"
      :class="labelClasses"
      :style="[labelStyles, labelStyle]"
    >
      {{ element.properties.label }}
    </label>
    <input
      :id="`input-${element.id}`"
      type="text"
      :name="element.properties.name"
      :value="displayValue"
      :aria-label="element.properties.label || element.properties.name"
      readonly
      :class="inputClasses"
      :data-log-field-name="logFieldNameAttr"
    >
  </div>
</template>

<style scoped>
  .indexer-title-element {
    font-family: var(--font-base);
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  .indexer-title-element.label-left {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .indexer-title-element.label-left label {
    flex-shrink: 0;
    white-space: nowrap;
  }

  .indexer-title-element.label-left input {
    flex: 1;
    min-width: 0;
    max-width: 100%;
    width: 0;
  }

  input {
    font-size: 11px;
    line-height: 16px;
  }

  input::placeholder {
    font-size: 10px;
    opacity: 0.6;
  }
</style>
